import styled from 'styled-components';

import { CARD_BORDER, CONTROL_BORDER, FOCUS_RING } from './base';

// ---------------------------------------------------------------------------
// Régua de Cobrança — miolos dos modais (PIX e gestão de raias). A casca do
// modal é o `components/Modal/Modal.js` compartilhado (FC7 cap.2 H.2).
// ---------------------------------------------------------------------------

export const ModalText = styled.div`
   font-size: 14px;
   color: #333;
   line-height: 1.55;
`;

// ─── PIX ──────────────────────────────────────────────────────────────────

export const PixQrPlaceholder = styled.div`
   height: 180px;
   border: 1px dashed rgba(0, 0, 0, 0.18);
   border-radius: 12px;
   background: #fafafa;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 6px;
   color: #9a9a9a;
   font-size: 12px;
   overflow: hidden;

   img {
      max-width: 100%;
      max-height: 100%;
   }
`;

export const PixCopyRow = styled.div`
   display: flex;
   gap: 8px;
`;

export const PixCodeInput = styled.input`
   height: 40px;
   flex: 1;
   min-width: 0;
   border: 1px solid ${CONTROL_BORDER};
   border-radius: 10px;
   padding: 0 12px;
   font: inherit;
   font-size: 12px;
   background: #fafafa;
   color: #444;
`;

// ─── Transferência de títulos entre trilhas (handoff 0103) ────────────────
// Seleção múltipla: o que atravessa a fronteira é o TÍTULO, não o caso — por
// isso a lista é o miolo do modal, não um detalhe.

// Caixa de contexto do destino (resposta do GET /:id/transfer-target): diz se
// os títulos entram num caso que já existe ou se um caso novo vai nascer.
export const TargetBox = styled.div`
   border: 1px solid ${CARD_BORDER};
   border-radius: 12px;
   padding: 12px 14px;
   background: #fafafa;
   font-size: 13px;
   color: #444;
   line-height: 1.55;
`;

export const TransferList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   max-height: 320px;
   overflow-y: auto;
`;

// Linha de título selecionável. É um <label> com checkbox real dentro — clique
// no texto marca, teclado funciona, sem `div onClick` (FC7 G.6).
export const TransferRow = styled.label`
   border: 1px solid
      ${({ $selected }) =>
         $selected ? 'rgba(64, 152, 239, 0.45)' : CARD_BORDER};
   background: ${({ $selected }) => ($selected ? '#f3f8fe' : '#fff')};
   border-radius: 12px;
   padding: 10px 12px;
   display: flex;
   align-items: center;
   gap: 12px;
   cursor: pointer;
   transition: background 120ms ease, border-color 120ms ease;

   &:hover {
      background: ${({ $selected }) => ($selected ? '#e9f2fd' : '#fafafa')};
   }

   &:focus-within {
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      box-shadow: ${FOCUS_RING};
   }

   input[type='checkbox'] {
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      cursor: pointer;
   }
`;

export const TransferRowInfo = styled.div`
   flex: 1;
   min-width: 0;
`;

export const TransferRowTitle = styled.div`
   font-size: 13px;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const TransferRowSub = styled.div`
   font-size: 11px;
   color: #8a8a8a;
   margin-top: 2px;
`;

export const TransferRowValue = styled.div`
   font-size: 13px;
   font-weight: 800;
   font-variant-numeric: tabular-nums;
   white-space: nowrap;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

// Barra "N de M selecionados · R$ X" + atalho de seleção.
export const TransferSelectionBar = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
   font-size: 12px;
   color: #666;
`;

export const TransferSelectionCount = styled.b`
   font-size: 12px;
   font-weight: 800;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   font-variant-numeric: tabular-nums;
`;

// ─── Raias ────────────────────────────────────────────────────────────────

export const StageList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

export const StageRow = styled.div`
   border: 1px solid ${CARD_BORDER};
   border-radius: 12px;
   padding: 12px 14px;
   display: flex;
   align-items: center;
   gap: 12px;
   background: #fff;
`;

export const StagePosition = styled.span`
   font-size: 12px;
   font-weight: 800;
   color: #8a8a8a;
   width: 18px;
   flex: 0 0 18px;
   font-variant-numeric: tabular-nums;
`;

export const StageInfo = styled.div`
   flex: 1;
   min-width: 0;
`;

export const StageName = styled.div`
   font-size: 13px;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const StageHint = styled.div`
   font-size: 11px;
   color: #8a8a8a;
   margin-top: 2px;
`;

// Botão "Auto/Manual" — só habilitado em raia ATTEMPT.
export const StageAutoToggle = styled.button`
   height: 28px;
   padding: 0 10px;
   border-radius: 999px;
   font-family: inherit;
   font-size: 11px;
   font-weight: 800;
   white-space: nowrap;
   cursor: pointer;
   transition: background 120ms ease, border-color 120ms ease;
   border: 1px solid
      ${({ $on }) => ($on ? 'rgba(64, 152, 239, 0.4)' : CONTROL_BORDER)};
   background: ${({ $on }) => ($on ? 'rgba(64, 152, 239, 0.12)' : '#fff')};
   color: ${({ $on }) => ($on ? '#0b3a63' : '#777')};

   &:hover:not(:disabled) {
      background: ${({ $on }) =>
         $on ? 'rgba(64, 152, 239, 0.18)' : '#f4f4f5'};
   }

   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
      color: #c9c9c9;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }
`;

export const StageDeleteButton = styled.button`
   width: 30px;
   height: 30px;
   flex: 0 0 30px;
   border-radius: 8px;
   border: 1px solid ${CONTROL_BORDER};
   background: #fff;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-family: inherit;
   color: #a01b1b;
   cursor: pointer;
   transition: background 120ms ease;

   &:hover:not(:disabled) {
      background: rgba(220, 38, 38, 0.08);
   }

   &:disabled {
      color: #d4d4d4;
      cursor: not-allowed;
      background: #fff;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }
`;

export const StageNewRow = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;
   margin-top: 4px;
`;

// "+ Nova raia" — botão claro com texto azul (40px).
export const StageAddButton = styled.button`
   height: 40px;
   padding: 0 16px;
   border-radius: 10px;
   border: 1px solid ${CONTROL_BORDER};
   background: #fff;
   color: #2f7bd8;
   font-weight: 700;
   font-size: 13px;
   font-family: inherit;
   cursor: pointer;
   white-space: nowrap;
   transition: background 120ms ease, border-color 120ms ease;

   &:hover:not(:disabled) {
      background: #f3f8fe;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
   }

   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }
`;
